import { Component, OnInit } from '@angular/core';
import { IGroup } from '@models/general/group';
import { AuthService } from 'src/app/core/auth/auth.service';
import { AdminService } from '../admin/admin.service';
import { TopBarService } from 'src/app/top-bar/top-bar.service';
import { ImportService } from './import.service';
import * as XLSX from 'xlsx';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from 'src/app/core/snackbar.service';

export interface IField {
  name: string;
  type: string;
  required: boolean;
  options?: {
    operation: string;
    fallback?: boolean;
    value: string[];
  };
}

@Component({
  selector: 'restoreid-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss']
})
export class ImportComponent implements OnInit {
  constructor(
    private _authService: AuthService,
    private _adminService: AdminService,
    private _topBarService: TopBarService,
    private _importService: ImportService,
    private _snackbarService: SnackbarService,
  ) {
    this._topBarService.setTitle('Import data');
  }

  public downloadLinkUrl: string = 'assets/excel-samples/RestoreID_';
  private _selectedType: string = '';
  private _selectedGroup: string = '';
  private _user: any;

  public potentialImportTypes: string[] = ['Location', 'Trap', 'Acoustic', 'eDNA', 'CMR'];
  public potentialGroups: IGroup[] = [];

  private _requiredColumns: string[] = [];
  private _currentTypeFields: IField[] = [];

  public columns: string[] = [];
  public readData: any[][] = [];
  public readyToImport: boolean = false;
  public successMessage: string = '';
  public importButtonDisabled: boolean = true;
  public errorTitle = 'Validation Error(s)';
  public validationErrors: string[] = [];

  async ngOnInit() {
    this._setCurrentUser();
    await this._getPotentialGroups();
  }

  set selectedType(value: string) {
    if (this._selectedType !== value) {
      this._selectedType = value;
      this._resetData();
      this._resetFileInput();
    }
  }

  get selectedType(): string {
    return this._selectedType;
  }

  set selectedGroup(value: string) {
    if (this._selectedGroup !== value) {
      this._selectedGroup = value;
    }
  }

  get selectedGroup(): string {
    return this._selectedGroup;
  }

  private async _getPotentialGroups() {
    if (this._authService.loggedInUser!.isadmin) {
      this.potentialGroups = await this._adminService.getGroups();
    }
    else {
      this.potentialGroups = await this._adminService.getUserGroups(this._authService.loggedInUserId!);
    }
  }

  private _setCurrentUser() {
    this._user = this._authService.loggedInUser;
  }

  get browseAreaVisible() {
    return this.selectedType !== '' && this.selectedGroup !== ''
  }

  get browseButtonDisabled() {
    return this.selectedType === '' || this.selectedGroup === ''
  }

  private _resetData() {
    this._requiredColumns = [];
    this._currentTypeFields = [];
    this.columns = [];
    this.readData = [];
    this.readyToImport = false;
    this.validationErrors = [];
    this.successMessage = '';
    this.importButtonDisabled = false;
    this.errorTitle = 'Validation Error(s)';
  }

  private _resetFileInput() {
    const selectedFile = (document.querySelector('input[type="file"]') as HTMLInputElement);
    if (selectedFile) {
      selectedFile.value = '';
    }
  }

  onFileChange(event: Event): void {
    this._resetData();

    const input = event.target as HTMLInputElement;

    if (!input.files?.length) return;

    const file = input.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheets = workbook.SheetNames;

      if (sheets.length > 0) {
        // Get the first sheet
        const firstSheetName: string = workbook.SheetNames[0];
        const worksheet: XLSX.WorkSheet = workbook.Sheets[firstSheetName];

        const sheetData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, defval: null }) as any[][];

        // Extract the first row as columns
        this.columns = sheetData[0] as string[];

        let validated = this._columnsValidation();
        if (validated) {
          const dataWithoutHeader = sheetData.slice(1); // Skip the first row
          dataWithoutHeader.forEach((row) => {
            const processedRow = this._processRowValues(row);
            this.readData.push(processedRow);
          });

          this._validateReadData();
          if (this.validationErrors.length === 0) {
            this.readyToImport = true;
          }
        }
      }
    };

    reader.readAsArrayBuffer(file);
  }

  private _columnsValidation(): boolean {
    this._mapColumns();

    const missingColumns = this._requiredColumns.filter(column => !this.columns.includes(column));
    if (missingColumns.length > 0) {
      this.validationErrors = missingColumns.map(column => {
        if (column === 'name') {
          if (this.selectedType === 'Location') {
            return 'Missing column: name_of_location';
          }
          else if (this.selectedType === 'Trap') {
            return 'Missing column: name_of_trap';
          }
        }
        return `Missing column: ${this._importService.getMappedValue(column)}`;
      });
      return false;
    }

    return true;
  }

  private _mapColumns() {
    switch (this.selectedType) {
      case 'Location':
        this._currentTypeFields = this._importService.mapColumnsToLocation();
        break;

      case 'Trap':
        this._currentTypeFields = this._importService.mapColumnsToTrap();
        break;

      case 'CMR':
        this._currentTypeFields = this._importService.mapColumnsToCMR();
        break;

      case 'eDNA':
        this._currentTypeFields = this._importService.mapColumnsToEDNA();
        break;

      case 'Acoustic':
        this._currentTypeFields = this._importService.mapColumnsToAcoustic();
        break;

      default:
        break;
    }

    // Add the keys to the first row of the imported data as header
    const keys = this._currentTypeFields
      .map(field => field.name);
    this.readData.push(keys);

    this._requiredColumns = this._currentTypeFields
      .filter(field => field.required)
      .map(field => field.name);

    this.columns = this.columns.map(column => this._importService.getMappedValue(column) || column);
  }

  private _processRowValues(row: any[]): any[] {
    return row.map(value => this._processCellValue(value));
  }

  private _processCellValue(value: any): any {

    if (typeof value === 'boolean') {
      return value;
    }
    else if (typeof value === 'number') {
      // Check if the number is a possible Excel date
      if (value > 25569 && value < 2958465) {
        return this._convertExcelDate(value);
      }
      return value;
    } else if(typeof value === 'string') {
      return value.trim();
    }

    return value;
  }

  private _convertExcelDate(excelDate: number): Date {
    const epoch = new Date(1899, 11, 30);
    return new Date(epoch.getTime() + excelDate * 86400000);
  }

  private _validateReadData() {
    this.validationErrors = [];

    this.readData.slice(1).forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        const field = this._currentTypeFields[columnIndex]; // Match column to field definition
        if (field) {
          //Check required fields
          if (field.required && (value === null || value === undefined || value === '')) {
            this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} is required.`);
          }

          switch (field.type) {
            case 'string':
              if (field.options) {
                if (field.options.operation == "in") {
                  // option is required and does not allow null
                  if (!field.options.value.includes(value) && value !== null && !field.options.fallback) {
                    this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be one of the following: ${field.options.value.join(', ')}.`);
                  }
                }
              }
              break;

            case 'number':
              if (isNaN(Number(value))) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a number.`);
              }
              break;

            case 'integer':
              if (!Number.isInteger(Number(value))) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be an integer.`);
              }
              break;

            case 'boolean':
              const isBooleanValue = (value === true || value === false || value === null);
              if (!isBooleanValue) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a true or false value.`);
              }
              break;

            case 'date':
            case 'timestamp':
              if (!(value instanceof Date)) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a date.`);
              }
              if (field.options?.operation === 'less' && value > new Date()) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be less than ${field.options.value}.`);
              }
              break;

            case 'uuid':
              if (!value.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/)) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a valid UUID.`);
              }
              break;

            case 'latitude':
              if (isNaN(Number(value))) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a number.`);
              }
              if (value < -90 || value > 90) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be between -90 and 90.`);
              }
              break;

            case 'longitude':
              if (isNaN(Number(value))) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be a number.`);
              }
              if (value < -180 || value > 180) {
                this.validationErrors.push(`${this._importService.getMappedValue(field.name)} of row ${rowIndex + 2} must be between -180 and 180.`);
              }
              break;

            default:
              break;
          }
        }
      });
    });
  }

  public async importData() {
    const selectedFile = (document.querySelector('input[type="file"]') as HTMLInputElement);
    if (selectedFile?.value == '') {
      this._snackbarService.openErrorMessage("Select a file to import.");
      return;
    }

    const rowCount = this.readData.length - 1;
    if (rowCount === 0) {
      this.validationErrors.push('No data to import.');
      return;
    }

    const data = this.readData.slice(1);
    const instanceColumns: string[] = this._currentTypeFields.map(field => field.name);

    try {
      await this._importService.importData(data, this.selectedType, instanceColumns, this._user.id, this.selectedGroup);

      this.importButtonDisabled = true;
      this.successMessage = `${rowCount} rows imported successfully.`;
    }
    catch (error) {
      if (error instanceof HttpErrorResponse) {
        const errorMessages = error.error.error;
        if (Array.isArray(errorMessages)) {
          this.validationErrors = errorMessages;
        }
        else {
          this.validationErrors.push(errorMessages);
        }
      }
      else {
        this.validationErrors.push('An error occurred while importing data.');
      }

      this.errorTitle = 'Import failed';
      this.validationErrors.unshift('0 rows imported.');
    }

    this._resetFileInput();
  }
}
